import React, { useEffect, useRef } from 'react';
import './splash.css';
import { useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import { useSound } from '../hooks/useSound';

const Splash = () => {
  const navigate = useNavigate();
  const { playHover, playClick } = useSound();

  const startRef     = useRef(null);
  const name1Ref      = useRef(null);
  const name2Ref      = useRef(null);
  const charRef        = useRef(null);
  const leftRef        = useRef(null);
  const rightRef       = useRef(null);
  const playBtnRef     = useRef(null);
  const viewLvlRef     = useRef(null);
  const yearRef        = useRef(null);
  const particlesRef   = useRef(null);

  /* ── ENTRANCE CHOREOGRAPHY ── */
  useEffect(() => {
    const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

    tl.fromTo(startRef.current,
      { opacity: 0, y: -20, letterSpacing: '2px' },
      { opacity: 1, y: 0, letterSpacing: '6.6px', duration: 0.6 }
    );

    tl.fromTo(name1Ref.current,
      { opacity: 0, y: -60, rotateX: 40, scale: 0.85 },
      { opacity: 1, y: 0, rotateX: 0, scale: 1, duration: 0.9, ease: 'back.out(1.4)' },
      '-=0.2'
    );
    tl.fromTo(name2Ref.current,
      { opacity: 0, y: -60, rotateX: 40, scale: 0.85 },
      { opacity: 1, y: 0, rotateX: 0, scale: 1, duration: 0.9, ease: 'back.out(1.4)' },
      '-=0.7'
    );

    tl.fromTo(charRef.current,
      { opacity: 0, y: 120, scale: 0.8 },
      { opacity: 1, y: 0, scale: 1, duration: 1, ease: 'elastic.out(0.9, 0.6)' },
      '-=0.5'
    );

    tl.fromTo(leftRef.current.children,
      { opacity: 0, x: -40 },
      { opacity: 1, x: 0, duration: 0.6, stagger: 0.12 },
      '-=0.6'
    );
    tl.fromTo(rightRef.current.children,
      { opacity: 0, x: 40 },
      { opacity: 1, x: 0, duration: 0.6, stagger: 0.12 },
      '-=0.7'
    );

    /* idle year-card flip-tease loop */
    gsap.to(yearRef.current, {
      rotateY: 360,
      duration: 6,
      repeat: -1,
      ease: 'none',
      delay: 2,
      repeatDelay: 4,
    });

    /* PLAY button — breathing glow loop once it lands */
    tl.call(() => {
      gsap.to(playBtnRef.current, {
        boxShadow: '0 0 45px rgba(216,244,132,0.5), 0 20px 60px rgba(0,0,0,0.45)',
        duration: 1.4,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
      });
    });

    return () => tl.kill();
  }, []);

  /* ── FLOATING PARTICLES (lightweight, pure CSS-driven via inline transforms) ── */
  useEffect(() => {
    const container = particlesRef.current;
    if (!container) return;
    const count = 22;
    const nodes = [];
    for (let i = 0; i < count; i++) {
      const dot = document.createElement('span');
      dot.className = 'splash-particle';
      const size = 2 + Math.random() * 3;
      dot.style.width = `${size}px`;
      dot.style.height = `${size}px`;
      dot.style.left = `${Math.random() * 100}%`;
      dot.style.top = `${Math.random() * 100}%`;
      dot.style.opacity = 0.15 + Math.random() * 0.35;
      container.appendChild(dot);
      nodes.push(dot);

      gsap.to(dot, {
        y: `-=${40 + Math.random() * 80}`,
        x: `+=${(Math.random() - 0.5) * 60}`,
        duration: 6 + Math.random() * 8,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
        delay: Math.random() * 4,
      });
    }
    return () => nodes.forEach(n => n.remove());
  }, []);

  /* ── MAGNETIC HOVER TILT for big buttons ── */
  const magneticMove = (e, ref, strength = 14) => {
    const rect = ref.current.getBoundingClientRect();
    const relX = (e.clientX - rect.left) / rect.width - 0.5;
    const relY = (e.clientY - rect.top) / rect.height - 0.5;
    gsap.to(ref.current, {
      x: relX * strength,
      y: relY * strength,
      rotateZ: relX * 4,
      duration: 0.4,
      ease: 'power2.out',
    });
  };
  const magneticReset = (ref) => {
    gsap.to(ref.current, { x: 0, y: 0, rotateZ: 0, duration: 0.5, ease: 'elastic.out(1, 0.4)' });
  };

  /* ── CLICK BURST (radial particle pop) ── */
  const burst = (e) => {
    const btn = e.currentTarget;
    const rect = btn.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    for (let i = 0; i < 10; i++) {
      const p = document.createElement('span');
      p.className = 'splash-burst-particle';
      p.style.left = `${cx}px`;
      p.style.top = `${cy}px`;
      btn.appendChild(p);
      const angle = (Math.PI * 2 * i) / 10;
      const dist = 30 + Math.random() * 25;
      gsap.to(p, {
        x: Math.cos(angle) * dist,
        y: Math.sin(angle) * dist,
        opacity: 0,
        scale: 0,
        duration: 0.6,
        ease: 'power2.out',
        onComplete: () => p.remove(),
      });
    }
  };

  /* ── NAV HANDLERS ── */
  const goLevels     = (e) => { playClick(); burst(e); setTimeout(() => navigate('/levels'), 180); };
  const goCharacters  = (e) => { playClick(); burst(e); setTimeout(() => navigate('/characters'), 180); };
  const goPlay         = (e) => { playClick(); burst(e); setTimeout(() => navigate('/level1'), 220); };

  return (
    <main className='splash'>
      <div className='splash-particles' ref={particlesRef} />

      <article className='start' ref={startRef}>
        <h5 className='splashsubtext'>START PLAYING WITH THE</h5>
      </article>

      <section className='namecont'>
        <div className='name1' ref={name1Ref} onMouseEnter={playHover}></div>
        <div className='name2' ref={name2Ref} onMouseEnter={playHover}></div>
      </section>

      <section className='splashbg'>
        <div className='charactersss' ref={charRef}></div>
      </section>

      <section className='splashcontent'>
        <article className='splashleft' ref={leftRef}>
          <div className='yearcont' ref={yearRef} onMouseEnter={playHover}>
            <h5 className='year'>20<br />26</h5>
          </div>

          <h5 className='sp1'>
            MOVE UP LEVELS BY PLAYING
            AND finishing puzzles
          </h5>

          <button
            className='viewlevels'
            ref={viewLvlRef}
            onClick={goLevels}
            onMouseEnter={playHover}
            onMouseMove={(e) => magneticMove(e, viewLvlRef, 8)}
            onMouseLeave={() => magneticReset(viewLvlRef)}
          >
            view levels
          </button>
        </article>

        <article className='splashright' ref={rightRef}>
          <h5 className='timee'>1960 - 2026</h5>

          <h5 className='solve'>
            SOLVE
            PUZZLES
            AND
            RIDDLES
          </h5>

          <div
            className='char2'
            onClick={goCharacters}
            onMouseEnter={playHover}
          ></div>

          <button
            className='playbtn'
            ref={playBtnRef}
            onClick={goPlay}
            onMouseEnter={playHover}
            onMouseMove={(e) => magneticMove(e, playBtnRef, 14)}
            onMouseLeave={() => magneticReset(playBtnRef)}
          >
            PLAY
          </button>
        </article>
      </section>
    </main>
  );
};

export default Splash;